import { useState } from "react";
import { FileDown, Loader2, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useSignupUrl } from "../utils/signup";

/**
 * Experimento «PDF escaneado limpio» (4-10-2026).
 *
 * Hipótesis: parte de quien llega a /id-scanner no busca extraer datos sino algo tipo CamScanner (foto → documento enderezado en
 * PDF), y las asesorías y la gente con papeleo preferirían hacerlo desde la web o por WhatsApp. Dos medidas baratas antes de
 * construir nada más:
 *   1. `free_intent`: una pregunta de una línea («¿qué buscabas?») con tres respuestas.
 *   2. `scan_pdf_click` / `scan_pdf_ready` / `scan_pdf_error`: ¿pulsan «Descargar PDF limpio»? (se hace en el navegador, sin subir la foto)
 * Regla de decisión (SEO.md): seguimos si ≥ 25 % de quien sube un documento de identidad elige PDF/«las dos» o pulsa el botón,
 * o si una página específica gana ≥ 100 impresiones/semana en 4 semanas. Si no, se retira.
 * Solo se muestra con fotos (no con PDF): es el caso de uso de una app de escaneo.
 */
const track = (event, params) => window.gtag && window.gtag("event", event, params || {});

const CHOICES = ["data", "pdf", "both"];

export default function ScanPdfPanel({ file, template, t, lang }) {
  const [choice, setChoice] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | working | done | error
  const signupHref = useSignupUrl(lang);

  const answer = (c) => {
    if (choice) return;
    setChoice(c);
    track("free_intent", { choice: c, template });
  };

  const download = async () => {
    if (status === "loading" || status === "working") return;
    track("scan_pdf_click", { template });
    setStatus("loading");
    try {
      const { loadOpenCv, cleanScanToPdf } = await import("../utils/cleanScan");
      await loadOpenCv();
      setStatus("working");
      const { blob, quadFound, ms } = await cleanScanToPdf(file);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${String(file.name || "scan").replace(/\.[^.]+$/, "")}-scan.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      track("scan_pdf_ready", { template, quad_found: quadFound, ms });
      setStatus("done");
    } catch (e) {
      track("scan_pdf_error", { template, reason: String((e && e.code) || "UNKNOWN").slice(0, 30) });
      setStatus("error");
    }
  };

  return (
    <div data-testid="scan-pdf-panel" className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">{t("scanPdf.title")}</p>

      <div className="mt-3">
        <p className="text-sm font-medium text-gray-800" id="scan-intent-q">
          {t("scanPdf.question")}
        </p>
        <div role="group" aria-labelledby="scan-intent-q" className="mt-2 flex flex-wrap gap-2">
          {CHOICES.map((c) => (
            <button
              key={c}
              type="button"
              disabled={Boolean(choice)}
              aria-pressed={choice === c}
              onClick={() => answer(c)}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                choice === c ? "border-primary-600 bg-primary-600 text-white" : "border-gray-300 text-gray-700 hover:border-primary-400 hover:bg-primary-50"
              } disabled:cursor-default ${choice && choice !== c ? "opacity-50" : ""}`}
            >
              {t(`scanPdf.choice.${c}`)}
            </button>
          ))}
        </div>
        {choice && <p className="mt-2 text-xs text-gray-500">{t("scanPdf.thanks")}</p>}
      </div>

      <div className="mt-4 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={download}
          disabled={status === "loading" || status === "working"}
          className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-gray-800 disabled:opacity-70"
        >
          {status === "loading" || status === "working" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
          {t("scanPdf.button")}
        </button>
        <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
          <ShieldCheck size={14} className="mt-0.5 shrink-0 text-primary-600" />
          <span>{t("scanPdf.privacy")}</span>
        </p>
        {(lang === "en" || lang === "es") && t("scanPdf.multi") !== "scanPdf.multi" && (
          <p className="mt-2 text-xs text-gray-500">
            {t("scanPdf.multi")}{" "}
            <Link to={`/${lang}/scan-to-pdf`} onClick={() => track("scan_pdf_multi_click", { template })} className="font-medium text-primary-700 underline">
              {t("scanPdf.multiLink")}
            </Link>
          </p>
        )}
        <p role="status" aria-live="polite" className="mt-2 text-sm text-gray-700">
          {status === "loading" && t("scanPdf.loading")}
          {status === "working" && t("scanPdf.working")}
          {status === "done" && t("scanPdf.done")}
          {status === "error" && <span className="text-red-700">{t("scanPdf.error")}</span>}
        </p>
        {status === "done" && (
          <p className="mt-2 text-sm text-gray-600">
            {t("scanPdf.cta")}{" "}
            <a
              href={signupHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("scan_pdf_signup_click", { template })}
              className="font-semibold text-primary-700 underline"
            >
              {t("scanPdf.ctaLink")}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
